// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ErrorService } from '@services/error.service';
import { NavService } from '@services/nav.service';
import { UserService } from '@services/user.service';
import { catchError, debounceTime, distinctUntilChanged, takeWhile } from 'rxjs/operators';
import { SuccessModalComponent } from 'src/app/modals/success-modal/success-modal.component';
import { AuthService } from '../services/auth.service';
import { LoginService } from '../services/login.service';
import { emailPattern } from 'src/app/utils/form-utils';

@Component({
    selector: 'app-reset-password-form',
    templateUrl: './reset-password-form.component.html',
    styleUrls: ['./reset-password-form.component.scss'],
})
export class ResetPasswordFormComponent implements OnInit, OnDestroy {
    error: string = null;
    isComponentAlive = true;
    isLoading = false;

    resetPasswordForm: FormGroup = new FormGroup({
        email: new FormControl('', [Validators.required, Validators.pattern(emailPattern)]),
        isPhone: new FormControl(false, [Validators.required]),
    });

    constructor(
        private readonly authService: AuthService,
        private readonly loginService: LoginService,
        private readonly dialog: MatDialog,
        private readonly navService: NavService,
        private readonly userService: UserService,
        private readonly errorService: ErrorService,
        private readonly router: Router
    ) {}

    ngOnInit() {
        this.navService.setTitle('Recover Password');

        this.resetPasswordForm.controls.email.valueChanges
            .pipe(
                takeWhile(() => this.isComponentAlive),
                debounceTime(500),
                distinctUntilChanged()
            )
            .subscribe((value) => {
                value.match(/\s+$/) && this.resetPasswordForm.controls.email.setValue(value.trim());
            });
    }

    // This will send the user a token
    resetPasswordSubmit(): void {
        if (this.resetPasswordForm.valid) {
            this.isLoading = true;

            this.loginService.setEmail(this.resetPasswordForm.value.email);
            this.userService
                .resetPassword(this.resetPasswordForm.value.email, this.resetPasswordForm.value.isPhone)
                .pipe(
                    takeWhile(() => this.isComponentAlive),
                    catchError((response) => {
                        this.isLoading = false;

                        if (response?.error?.statusCode === 500) {
                            this.errorService.showError({
                                statusCode: 500,
                                message: this.authService.determineAuthErrorMessage(response.error),
                                title: 'Login Attempt Unsuccessful',
                            });
                            throw response;
                        } else {
                            this.errorService.showGenericError();
                            throw response;
                        }
                    })
                )
                .subscribe((result) => {
                    // resetting password with account already set up
                    if (!result) {
                        this.errorService.showGenericError();
                    } else if (result.includes('Pin')) {
                        // a temporary password has been sent to the user, redirect to the reset form where they set their password
                        this.router.navigate(['/login', 'reset-token']);
                    }
                    // trying to reset password without going through account set up
                    else if (result.includes('Admin')) {
                        this.dialog
                            .open(SuccessModalComponent, {
                                width: '700px',
                                data: {
                                    title: 'Welcome Link Resent',
                                    content: 'Please check your phone/email.',
                                },
                            })
                            .afterClosed()
                            .pipe(takeWhile(() => this.isComponentAlive))
                            .subscribe(() => this.router.navigate(['/login']));
                    } else {
                        // if it is something else than above, assuming it failed
                        this.errorService.showGenericError();
                    }
                    this.isLoading = false;
                });
        }
    }

    // Helpers
    fieldError(form: FormGroup, name: string): string {
        const ctl = form.get(name);

        if (ctl.hasError('required')) {
            return 'You must enter a value';
        } else if (ctl.hasError('email')) {
            return 'Invalid email';
        }
        if (!ctl.value.match(emailPattern)) {
            return 'Invalid Email Address';
        } else {
            return null;
        }
    }

    ngOnDestroy(): void {
        this.isComponentAlive = false;
    }
}
